import { useDebounce } from '@uidotdev/usehooks';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Page, PageProps } from 'react-pdf';
// @ts-expect-error cannot find module
import { PageCallback } from 'react-pdf/dist/cjs/shared/types.js';

import { OcrItem, useAttentionMap, useCanvasOverlay, useDrawBoxes, useFilterOcrItems, useSelectionBox } from '@/hooks';
import { useDocumentStore } from '@/store';
import { attentionMapToBlocks, Box } from '@/utils';

import { OcrPopover } from './OcrPopover';

export type DocumentPageProps = {
  page: number;
} & PageProps;

const IDocumentPage = ({ page, ...rest }: DocumentPageProps) => {
  const zoom = useDocumentStore((state) => state.zoom);
  const focusedField = useDocumentStore((state) => state.focusedField);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rotation = useDocumentStore((state) => state.rotation);

  const [baseRotation, setBaseRotation] = useState(0);
  const [renderSuccess, setRenderSuccess] = useState(false);
  const [canvasVersion, setCanvasVersion] = useState(0);

  useEffect(() => setRenderSuccess(false), [rotation, zoom]);

  const attentionMap = useMemo(
    () => (page - 1 === focusedField?.page ? attentionMapToBlocks(focusedField?.attentionMap) : []),
    [focusedField?.attentionMap, focusedField?.page, page]
  );

  const { overlayRef: attentionMapCanvasRef, layerVersion: attentionMapLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  useAttentionMap(attentionMapCanvasRef, attentionMap, rotation, attentionMapLayerVersion);

  const { overlayRef: ocrLayerCanvasRef, layerVersion: ocrLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  const [ocrItems, setOcrItems] = useState<OcrItem[]>([]);
  useDrawBoxes(ocrLayerCanvasRef, ocrItems, rotation, ocrLayerVersion);

  const { overlayRef: selectionCanvasRef, layerVersion: selectionLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  const [selectionBox, setSelectionBox] = useState<Box | undefined>();
  const debouncedSelectionBox = useDebounce(selectionBox, 50);

  const { handleMouseDown, handleMouseMove, handleMouseUp, clear } = useSelectionBox({
    canvasRef: selectionCanvasRef,
    onSelectMove: (box) => setSelectionBox(box),
    onSelectEnd: (box) => setSelectionBox(box),
    onSelectStart: (box) => setSelectionBox(box),
    canvasVersion: selectionLayerVersion,
  });

  const {
    items: filteredOcrItems,
    anchor: ocrAnchor,
    text: ocrText,
  } = useFilterOcrItems({
    selectionBox: debouncedSelectionBox,
    canvasRef,
    rotation,
    page: page - 1,
    canvasVersion: selectionLayerVersion,
    scaleAnchorByDpr: true,
  });

  useEffect(() => setOcrItems(filteredOcrItems), [filteredOcrItems]);
  const dismissOcrPopover = useCallback(() => {
    setSelectionBox(undefined);
    clear();
  }, [clear]);

  const onRenderSuccess = useCallback((page: PageCallback) => {
    setBaseRotation(page.rotate);
    setRenderSuccess(true);
    setCanvasVersion((v) => v + 1);
  }, []);

  return (
    <div className={!renderSuccess ? 'hidden' : 'animate-fade-in-fast flex w-full justify-center !bg-gray-200'}>
      <Page
        key={page}
        canvasRef={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onRenderSuccess={onRenderSuccess}
        pageNumber={page}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        rotate={baseRotation + rotation}
        scale={zoom}
        {...rest}
      >
        {ocrAnchor && ocrText ? <OcrPopover anchor={ocrAnchor} text={ocrText} onDismiss={dismissOcrPopover} /> : null}
      </Page>
    </div>
  );
};

export const DocumentPage = memo(IDocumentPage) as typeof IDocumentPage;
